import React, { useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import usePermission from "provider/PermissionProvider";
import { CONFIGURATION_KEY } from "provider/PermissionProvider/permissions";
import toastr from "toastr";
import AddConfiguration from "./add-configuration";
import AddFirmware from "./add-firmware";
import Summary from "./summary";
import {
  AddConfig,
  UpdateConfig,
  IsConfigNameWithFamily,
  GetInitialConfiguration,
  GetConfiguration,
  GetElementsProperties,
} from "./action";
import Loader from "../../Loader";
import { ElementStatus, DataType, Mode, FieldToMatch } from "./constant";
import moment from "moment";
import { getFamilyUnitVersionInConfigurations } from "helper/utils";

const Setup = (props) => {
  const dispatch = useDispatch();
  const { permissionContext } = usePermission();
  const { hasFullPermission } = permissionContext.permissions;

  const [configId, setConfigId] = useState("");
  const [configName, setConfigName] = useState("");
  const [fieldToMatch, setFieldToMatch] = useState(
    FieldToMatch.Configuration |
      FieldToMatch.ElementObject |
      FieldToMatch.ElementField
  );
  const [mode, setMode] = useState(Mode.NEW);
  const [configurations, setConfigurations] = useState([]);
  const [firmwares, setFirmwares] = useState([]);
  const [releaseHistory, setReleaseHistory] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage] = useState("Loading configuration");
  const [showConfigurationSetup, setShowConfigurationSetup] = useState(true);
  const [showFirmwareSetup, setShowFirmwareSetup] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [unitFamilyId, setUnitFamilyId] = useState(0);
  const [unitTypeId, setUnitTypeId] = useState(0);
  const [unitVersionId, setUnitVersionId] = useState(0);
  const [isMandatoryUpdate, setIsMandatoryUpdate] = useState(false);

  const processInitialConfigurations = (configuration, mode) => {
    if (mode === Mode.NEW) {
      //default element path
      let elementPath = "configuration.elements";

      let grouped = configuration.configurations.filter((x) => {
        if (
          x.dataType === DataType.Object &&
          x.path.length > elementPath.length &&
          x.path.length < elementPath.length + 4
        ) {
          //add 4 to add the length index bracket "[0]".
          return x;
        }
      });

      let new_firmwares = [];
      let seq = 0;
      grouped.forEach((element) => {
        new_firmwares.push({
          id: "",
          path: element.path,
          fileId: "",
          fileName: "",
          fileSize: 0,
          fileType: "", 
          sha256: "",
          sequence: seq++,
          isDeleted: false,
          isRequired: false,
          hasChanged: false,
          elementStatus: ElementStatus.NEW,
          dateUploaded: moment().unix(),
        });
      });

      setConfigurations(configuration.configurations);
      setFirmwares(new_firmwares);
      setIsMandatoryUpdate(false); //default false if new 
    } else {
      //MODE_EDIT
      setConfigId(configuration.configId);
      setConfigName(configuration.configName);
      setFieldToMatch(configuration.fieldToMatch);
      setConfigurations(configuration.configurations);
      setFirmwares(configuration.firmwares);
      setIsMandatoryUpdate(configuration.isMandatoryUpdate ?? false); // set isMandatoryUpdate as returned by GetConfiguration 
    }
  };

  useEffect(() => {
    const id = props.match.params.id;
    dispatch(GetElementsProperties());
    if (id === undefined) {
      setUnitFamilyId(
        props.location.state ? props.location.state.selectedUnitFamily : 47
      );
      setUnitTypeId(
        props.location.state ? props.location.state.selectedUnitType : 1
      );
      setUnitVersionId(
        props.location.state ? props.location.state.selectedUnitVersion : -1
      );
      dispatch(
        GetInitialConfiguration(
          props.location.state ? props.location.state.selectedUnitFamily : 47, //Defaults to MIXIT
          props.location.state ? props.location.state.selectedUnitType : 1, //Defaults to Generation 3
          props.location.state ? props.location.state.selectedUnitVersion : -1
        )
      ).then((configuration) => {
        processInitialConfigurations(configuration, Mode.NEW);
        setIsLoading(false);
        setMode(Mode.NEW);
      });
    } else {
      dispatch(GetConfiguration(id)).then((configuration) => {
        processInitialConfigurations(configuration, Mode.EDIT);
        setIsLoading(false);
        setMode(Mode.EDIT);

        const familyUnitTypes = getFamilyUnitVersionInConfigurations(
          configuration.configurations
        );
        setUnitFamilyId(familyUnitTypes.unitFamilyId);
        setUnitTypeId(familyUnitTypes.unitTypeId);
        setUnitVersionId(familyUnitTypes.unitVersionId);
        setReleaseHistory(configuration.releaseHistory);

        if (
          !hasFullPermission(familyUnitTypes.unitFamilyId, CONFIGURATION_KEY)
        ) {
          props.history.push({
            pathname: "/forbidden",
          });
        }
      });
    }
  }, []);

  const onConfigurationSetupNext = async (
    configName,
    fieldToMatch,
    configurations,
    firmwares,
    isMandatoryUpdate
  ) => {
    const response = await dispatch(
      IsConfigNameWithFamily(configName, unitFamilyId)
    );
    if (response.isConfigNameExist && mode === Mode.NEW) {
      toastr.error("Configuration name already exist.", "Error");
    } else {
      setConfigName(configName);
      setFieldToMatch(fieldToMatch);
      setConfigurations(configurations);
      setFirmwares(firmwares);
      setShowFirmwareSetup(true);
      setShowConfigurationSetup(false);
      setShowSummary(false);
      setIsMandatoryUpdate(isMandatoryUpdate); 
    }
  };

  const onFirmwareSetupNext = (firmwares) => {
    setFirmwares(firmwares);
    setShowFirmwareSetup(false);
    setShowConfigurationSetup(false);
    setShowSummary(true);
  };

  const onFirmwareSetupBack = (firmwares) => {
    setFirmwares(firmwares);
    setShowFirmwareSetup(false);
    setShowConfigurationSetup(true);
    setShowSummary(false);
  };

  const onSummaryBack = () => {
    setShowFirmwareSetup(true);
    setShowConfigurationSetup(false);
    setShowSummary(false);
  };

  const onSave = async () => {
    if (mode === Mode.NEW) {
      const response = await dispatch(
        AddConfig(
          configName,
          fieldToMatch,
          configurations,
          firmwares,
          unitFamilyId,
          unitTypeId,
          unitVersionId,
          isMandatoryUpdate
        )
      );

      setIsLoading(false);
      if (response.isSuccess) {
        toastr.info(`configuration ${configName} successfully saved.`, "Save");
        props.history.push({
          pathname: "/Configuration",
        });
      } else {
        toastr.error(`${response.payload.error}`, "Error");
      }
    } else {
      const response = await dispatch(
        UpdateConfig(
          configId,
          configName,
          fieldToMatch,
          configurations,
          firmwares,
          isMandatoryUpdate,
          releaseHistory
        )
      );
      setIsLoading(false);
      if (response.isSuccess) {
        toastr.info(
          `configuration ${configName} successfully updated.`,
          "Update"
        );
        props.history.push({
          pathname: "/Configuration",
        });
      } else {
        toastr.error(`${response.payload.error}`, "Error");
      }
    }
  };

  if (isLoading) {
    return <Loader fullpage message={loadingMessage} />;
  }

  return (
    <div>
      {showConfigurationSetup ? (
        <AddConfiguration
          id="configuration_setup"
          history={props.history}
          configName={configName}
          fieldToMatch={fieldToMatch}
          configurations={configurations}
          firmwares={firmwares}
          isMandatoryUpdate={isMandatoryUpdate}
          onNext={onConfigurationSetupNext}
        />
      ) : null}
      {showFirmwareSetup ? (
        <AddFirmware
          id="firmware_setup"
          configName={configName}
          configurations={configurations}
          firmwares={firmwares}
          onBack={onFirmwareSetupBack}
          onNext={onFirmwareSetupNext}
        />
      ) : null}
      {showSummary ? (
        <Summary
          id="summary"
          configName={configName}
          isMandatoryUpdate={isMandatoryUpdate}
          configurationId={configId} 
          configurations={configurations}
          firmwares={firmwares}
          releaseHistory={releaseHistory}
          onBack={onSummaryBack}
          onSave={onSave}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    configId: state.configuration.configId,
    configName: state.configuration.configName,
    fieldToMatch: state.configuration.fieldToMatch,
    firmwares: state.configuration.firmwares,
    configurations: state.configuration.configurations,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    AddConfig: (config) => dispatch(AddConfig(config)),
    dispatch,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Setup);
